import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { LOAN_TERM, PERSONAL_NUMBER } from '~/actions/actionTypes';
import { setInput } from '../../actions/formActions';
import { getDefaultValue } from '../LoanTerm/LoanTermService';
import { IConfig, IPage, ISettings, TPageKeys } from '~/sharedInterfaces/SharedInterfaces';
import { IErrorState, IState } from '~/sharedInterfaces/IState';
import { IErrorWithValidation } from '~/services/ErrorMessageService';
import GetComponent from '../../services/GetComponent';
import SuccessMessage from '../SuccessMessage/SuccessMessage';

const ErrorFallback: FC<any> = ({ error }) => (
  <div role="alert">
    <p className="qred-first">
      <span className="qred-first"> Something went wrong:</span>
    </p>
    <pre>
      <span className="qred-first">{error.message}</span>
    </pre>
  </div>
);

interface IPageProps {
  pageName: string;
  page: IPage;
  settings: ISettings;
  activeClass?: boolean;
}

const Page: FC<IPageProps> = ({ pageName, page, settings, activeClass = false }) => {
  const errors = useSelector((state: IState) => state.error);
  const { signupNewCustomer } = useSelector((state: IState) => state.widgetContext);
  let errorInPage = false;
  const componentList = Object.keys(page).map((componentKey, i: number) => {
    const componentName = componentKey as TPageKeys;
    const error = errors[componentKey as keyof IErrorState] as IErrorWithValidation;
    if (!errorInPage && error && (error.missing || error.invalid)) errorInPage = true;
    if (
      page.personalNumber?.componentContext?.hideInRlc &&
      componentName === PERSONAL_NUMBER &&
      signupNewCustomer === false
    ) {
      return null;
    }

    return (
      <GetComponent
        {...page[componentName]}
        componentName={componentName}
        // eslint-disable-next-line react/no-array-index-key
        key={`${settings.id}-${componentName}-${i}`}
        settings={settings}
      />
    );
  });

  return (
    <div
      className={`qred-page${` ${pageName}`}${activeClass ? ' qred-active' : ''}${
        errorInPage ? ' qred-error' : ''
      }`}
    >
      {componentList}
    </div>
  );
};

const onSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => event.preventDefault();

const Form: FC<IConfig> = ({ pages, settings }) => {
  const error = useSelector((state: IState) => state.error.serverError);
  const { currentPage, widgetFinished, loading } = useSelector(
    (state: IState) => state.widgetContext
  );
  const pageName = `qred-${currentPage}`;
  const dispatch = useDispatch();

  const getFormClasses = () => {
    const parts = [];

    if (error) parts.push('qred-error');
    if (loading) parts.push('qred-loading');

    if (parts.length > 0) return parts.join(' ');
    return '';
  };

  useEffect(() => {
    const loanTermsExist = pages.some((page) =>
      Object.keys(page).some((component) => component === LOAN_TERM)
    );

    if (!loanTermsExist && settings.widgetType === 'loan') {
      dispatch(setInput(LOAN_TERM, getDefaultValue(settings.market).toString()));
    }
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {widgetFinished ? (
        <SuccessMessage />
      ) : (
        <form
          name={settings.id}
          onSubmit={onSubmitHandler}
          className={`qred-widget ${getFormClasses()}`}
        >
          {settings.pagination ? (
            <Page pageName={pageName} activeClass page={pages[currentPage]} settings={settings} />
          ) : (
            pages.map((page, i: number) => {
              const name = `qred-${i}`;
              return (
                <Page
                  key={name}
                  activeClass={currentPage === i}
                  pageName={name}
                  page={page}
                  settings={settings}
                />
              );
            })
          )}
        </form>
      )}
    </ErrorBoundary>
  );
};

export default Form;
